import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Space, Tooltip, Popover, Checkbox, Divider, } from "antd";
import { SettingOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, UnlockOutlined, LockOutlined, } from "@ant-design/icons";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
// 在组件顶部添加配置常量
const MIN_COLUMN_WIDTH = 60; // 最小列宽
const MAX_COLUMN_WIDTH = 600; // 最大列宽
// 图标映射
const iconMap = {
    PlusOutlined: React.createElement(PlusOutlined),
    ReloadOutlined: React.createElement(ReloadOutlined),
    UnlockOutlined: React.createElement(UnlockOutlined),
    LockOutlined: React.createElement(LockOutlined),
    DeleteOutlined: React.createElement(DeleteOutlined),
    EditOutlined: React.createElement(EditOutlined),
    SettingOutlined: React.createElement(SettingOutlined),
};
// 添加ResizableTitle组件
const ResizableTitle = (props) => {
    const { onResize, width, ...restProps } = props;
    if (!width) {
        return _jsx("th", { ...restProps });
    }
    return (_jsx(Resizable, { width: width, height: 0, onResize: onResize, draggableOpts: { enableUserSelectHack: false }, 
        // 添加最小最大宽度限制
        minConstraints: [MIN_COLUMN_WIDTH, 0], maxConstraints: [MAX_COLUMN_WIDTH, 0], handle: _jsx("span", { className: "react-resizable-handle", onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
            }, style: {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "col-resize",
                zIndex: 100,
            } }), children: _jsx("th", { ...restProps, style: { ...restProps.style, position: "relative" } }) }));
};
/**
 * 通用表格面板组件
 * 包含标题、按钮、表格和分页
 */
function GenericPanelTable({ title, buttons = [], tableProps, loading = false, refreshData = () => { }, showSelection = false, cardProps = {}, extra, pagination, onPaginationChange, defaultPageSize = 10, showColumnSetting = true, storageKey, resizableColumns = false, saveColumnWidths = true, defaultActions, headerPadding, }) {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [popoverVisible, setPopoverVisible] = useState(false);
    // 内部分页状态（当没有传入分页时使用）
    const [internalPagination, setInternalPagination] = useState({
        current: 1,
        pageSize: defaultPageSize,
        total: 0,
    });
    // 添加列宽状态
    const [columnWidths, setColumnWidths] = useState({});
    // 列可见性状态
    const [columnVisibility, setColumnVisibility] = useState({});
    // 使用外部传入的分页或内部分页
    // 如果 tableProps.pagination 明确设置为 false，则不显示分页
    const currentPagination = tableProps.pagination === false ? false : pagination || internalPagination;
    // 获取表格所有列
    const allColumns = useMemo(() => tableProps.columns || [], [tableProps.columns]);
    // 存储键，优先使用传入值，否则使用标题（转为字符串）或默认值
    const actualStorageKey = useMemo(() => storageKey ||
        (typeof title === "string"
            ? `table_columns_${title}`
            : "table_columns_default"), [storageKey, title]);
    // 为每列生成唯一标识
    const getColumnKey = useCallback((column) => {
        return (column.dataIndex || column.key || column.title)?.toString() || "";
    }, []);
    // 过滤显示的列 - 只定义一次
    const visibleColumns = allColumns.filter((column) => {
        const key = getColumnKey(column);
        return !key || columnVisibility[key] !== false;
    });
    // 初始化列宽
    useEffect(() => {
        if (resizableColumns && saveColumnWidths) {
            try {
                const savedWidths = localStorage.getItem(`${actualStorageKey}_widths`);
                if (savedWidths) {
                    setColumnWidths(JSON.parse(savedWidths));
                }
            }
            catch (error) {
                console.error("加载列宽配置失败:", error);
            }
        }
    }, [resizableColumns, saveColumnWidths, actualStorageKey]);
    // 保存列宽到本地存储
    useEffect(() => {
        if (resizableColumns &&
            saveColumnWidths &&
            Object.keys(columnWidths).length > 0) {
            try {
                localStorage.setItem(`${actualStorageKey}_widths`, JSON.stringify(columnWidths));
            }
            catch (error) {
                console.error("保存列宽配置失败:", error);
            }
        }
    }, [columnWidths, resizableColumns, saveColumnWidths, actualStorageKey]);
    // 处理列宽调整
    const handleResize = (index) => (e, { size }) => {
        const newColumnWidths = { ...columnWidths };
        const column = visibleColumns[index];
        const key = getColumnKey(column);
        if (key) {
            // 添加额外的宽度限制逻辑
            let newWidth = size.width;
            // 确保宽度在允许的范围内
            if (newWidth < MIN_COLUMN_WIDTH)
                newWidth = MIN_COLUMN_WIDTH;
            if (newWidth > MAX_COLUMN_WIDTH)
                newWidth = MAX_COLUMN_WIDTH;
            newColumnWidths[key] = newWidth;
            setColumnWidths(newColumnWidths);
        }
    };
    // 处理表格组件
    const components = resizableColumns
        ? {
            header: {
                cell: ResizableTitle,
            },
        }
        : undefined;
    // 添加列宽重置函数
    const resetColumnWidths = () => {
        setColumnWidths({});
        try {
            localStorage.removeItem(`${actualStorageKey}_widths`);
        }
        catch (error) {
            console.error("移除列宽配置失败:", error);
        }
        setPopoverVisible(false);
        // 不刷新页面，而是重新进行认证检查
        // setTimeout(() => {
        //   window.location.reload();
        // }, 100);
    };
    // 设置列宽和处理函数
    const resizedColumns = visibleColumns.map((col, index) => {
        const key = getColumnKey(col);
        // 修改此处逻辑，避免默认宽度过大
        const savedWidth = key && columnWidths[key] ? columnWidths[key] : undefined;
        // 仅当有保存的宽度时才使用，否则使用列自身的宽度或默认最小宽度
        const width = savedWidth || col.width || MIN_COLUMN_WIDTH;
        return {
            ...col,
            width,
            onHeaderCell: (column) => ({
                width,
                onResize: handleResize(index),
            }),
        };
    });
    // 初始化列可见性
    useEffect(() => {
        // 尝试从本地存储加载配置
        try {
            const savedConfig = localStorage.getItem(actualStorageKey);
            if (savedConfig) {
                // 解析保存的配置
                const parsedConfig = JSON.parse(savedConfig);
                setColumnVisibility(parsedConfig);
            }
            else {
                // 没有保存的配置，初始化为全部可见
                const initialVisibility = {};
                allColumns.forEach((column) => {
                    const key = getColumnKey(column);
                    if (key) {
                        initialVisibility[key] = true;
                    }
                });
                setColumnVisibility(initialVisibility);
            }
        }
        catch (error) {
            console.error("加载列配置失败:", error);
            // 出错时，默认所有列可见
            const defaultVisibility = {};
            allColumns.forEach((column) => {
                const key = getColumnKey(column);
                if (key) {
                    defaultVisibility[key] = true;
                }
            });
            setColumnVisibility(defaultVisibility);
        }
    }, [actualStorageKey, allColumns, getColumnKey]);
    // 保存列可见性到本地存储
    useEffect(() => {
        // 仅当columnVisibility不为空对象时保存
        if (Object.keys(columnVisibility).length > 0) {
            try {
                localStorage.setItem(actualStorageKey, JSON.stringify(columnVisibility));
            }
            catch (error) {
                console.error("保存列配置失败:", error);
            }
        }
    }, [columnVisibility, actualStorageKey]);
    // 处理列可见性变化
    const handleColumnVisibilityChange = (checkedValues) => {
        const newVisibility = {};
        // 先将所有列设为不可见
        allColumns.forEach((column) => {
            const key = getColumnKey(column);
            if (key) {
                newVisibility[key] = false;
            }
        });
        // 再将选中的列设为可见
        checkedValues.forEach((value) => {
            newVisibility[value] = true;
        });
        setColumnVisibility(newVisibility);
    };
    // 重置列可见性，恢复所有列显示
    const resetColumnVisibility = () => {
        const resetVisibility = {};
        allColumns.forEach((column) => {
            const key = getColumnKey(column);
            if (key) {
                resetVisibility[key] = true;
            }
        });
        setColumnVisibility(resetVisibility);
        setPopoverVisible(false);
    };
    // 处理表格选择变化
    const handleSelectionChange = (newSelectedRowKeys, newSelectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };
    // 处理表格变化
    const handleTableChange = (newPagination, filters, sorter, extra) => {
        // 如果有外部传入的回调，则调用
        if (onPaginationChange) {
            onPaginationChange(newPagination);
        }
        else {
            // 否则使用内部状态
            setInternalPagination(newPagination);
        }
        // 确保传递所有参数
        if (tableProps.onChange) {
            tableProps.onChange(newPagination, filters, sorter, extra);
        }
    };
    // 列设置弹窗内容
    const columnSettingContent = (_jsxs("div", { style: { maxWidth: 300 }, children: [_jsxs("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                }, children: [_jsx("span", { style: { fontWeight: "bold" }, children: "\u663E\u793A\u7684\u5217" }), _jsxs(Space, { children: [_jsx(Button, { type: "link", size: "small", onClick: resetColumnVisibility, title: "\u91CD\u7F6E\u5217\u663E\u793A", style: { fontSize: "12px" }, children: "\u91CD\u7F6E\u663E\u793A" }), _jsx(Button, { type: "link", size: "small", onClick: resetColumnWidths, title: "\u91CD\u7F6E\u5217\u5BBD", danger: true, style: { fontSize: "12px" }, children: "\u91CD\u7F6E\u5BBD\u5EA6" })] })] }), _jsx(Divider, { style: { margin: "8px 0" } }), _jsx(Checkbox.Group, { style: { display: "flex", flexDirection: "column", gap: "8px" }, value: Object.entries(columnVisibility)
                    .filter(([_, visible]) => visible)
                    .map(([key]) => key), onChange: handleColumnVisibilityChange, children: allColumns.map((column) => {
                    const key = getColumnKey(column);
                    if (!key)
                        return null;
                    const title = typeof column.title === "string" ? column.title : `列 ${key}`;
                    return (_jsx(Checkbox, { value: key, children: title }, key));
                }) })] }));
    // 渲染按钮组
    const renderButtonGroup = (groupType) => {
        const groupButtons = buttons.filter((button) => button.group === groupType);
        return (_jsx(Space, { children: groupButtons.map((button) => {
                // 检查按钮是否应该隐藏
                const isHidden = typeof button.hidden === "function"
                    ? button.hidden(selectedRows)
                    : button.hidden;
                if (isHidden)
                    return null;
                // 检查按钮是否应该禁用
                const isDisabled = typeof button.disabled === "function"
                    ? button.disabled(selectedRows)
                    : button.disabled;
                // 处理图标：如果是字符串则从映射中获取，否则直接使用
                const iconElement = typeof button.icon === "string"
                    ? iconMap[button.icon] || button.icon
                    : button.icon;
                return (_jsx(Button, { type: button.type === "danger-border" ? "default" : button.type, icon: iconElement, disabled: isDisabled, size: "small", style: {
                        color: isDisabled
                            ? "rgba(0, 0, 0, 0.25)"
                            : button.type === "primary"
                                ? "#fff"
                                : button.type === "danger-border"
                                    ? "#ff4d4f"
                                    : "#1f1f1f",
                        ...(button.type === "text" && !isDisabled && { color: "#666" }),
                        ...(button.type === "primary" &&
                            !isDisabled && {
                            backgroundColor: "#1890ff",
                            borderColor: "#1890ff",
                        }),
                        ...(button.type === "danger-border" &&
                            !isDisabled && {
                            backgroundColor: "transparent",
                            borderColor: "#ff4d4f",
                            borderWidth: "1px",
                            borderStyle: "solid",
                        }),
                        fontSize: "12px",
                    }, onClick: () => button.onClick(selectedRows, refreshData), ghost: button.ghost, danger: button.danger, children: button.text }, button.key));
            }) }));
    };
    // 渲染所有按钮分组
    const renderButtons = () => {
        const primaryButtons = buttons.filter((button) => button.group === "primary");
        const secondaryButtons = buttons.filter((button) => button.group === "secondary");
        const tertiaryButtons = buttons.filter((button) => button.group === "tertiary");
        const ungroupedButtons = buttons.filter((button) => !button.group);
        return (_jsxs("div", { style: { display: "flex", alignItems: "center", gap: "24px" }, children: [tertiaryButtons.length > 0 && renderButtonGroup("tertiary"), secondaryButtons.length > 0 && renderButtonGroup("secondary"), primaryButtons.length > 0 && renderButtonGroup("primary"), ungroupedButtons.length > 0 && (_jsx(Space, { children: ungroupedButtons.map((button) => {
                        const isHidden = typeof button.hidden === "function"
                            ? button.hidden(selectedRows)
                            : button.hidden;
                        if (isHidden)
                            return null;
                        const isDisabled = typeof button.disabled === "function"
                            ? button.disabled(selectedRows)
                            : button.disabled;
                        // 处理图标：如果是字符串则从映射中获取，否则直接使用
                        const iconElement = typeof button.icon === "string"
                            ? iconMap[button.icon] || button.icon
                            : button.icon;
                        return (_jsx(Button, { type: button.type === "danger-border" ? "default" : button.type, icon: iconElement, disabled: isDisabled, size: "small", style: {
                                color: isDisabled
                                    ? "rgba(0, 0, 0, 0.25)"
                                    : button.type === "primary"
                                        ? "#fff"
                                        : button.type === "danger-border"
                                            ? "#ff4d4f"
                                            : "#1f1f1f",
                                ...(button.type === "text" &&
                                    !isDisabled && { color: "#666" }),
                                ...(button.type === "primary" &&
                                    !isDisabled && {
                                    backgroundColor: "#1890ff",
                                    borderColor: "#1890ff",
                                }),
                                ...(button.type === "danger-border" &&
                                    !isDisabled && {
                                    backgroundColor: "transparent",
                                    borderColor: "#ff4d4f",
                                    borderWidth: "1px",
                                    borderStyle: "solid",
                                }),
                                fontSize: "12px",
                            }, onClick: () => button.onClick(selectedRows, refreshData), ghost: button.ghost, danger: button.danger, children: button.text }, button.key));
                    }) }))] }));
    };
    // 修改 titleWithSettings 组件中的 Popover 配置
    const titleWithSettings = (_jsxs(Space, { children: [_jsx("span", { style: { color: "#1f1f1f", fontWeight: 500 }, children: title }), showColumnSetting && (_jsx(Tooltip, { title: "\u5217\u8BBE\u7F6E", children: _jsx(Popover, { content: columnSettingContent, title: null, trigger: "click", open: popoverVisible, onOpenChange: setPopoverVisible, placement: "rightTop" // 修改为 rightTop，使弹出框出现在右侧
                    , overlayStyle: { minWidth: "60px" }, children: _jsx(Button, { icon: React.createElement(SettingOutlined), type: "text", size: "small", style: { color: "#666" }, onClick: (e) => {
                            e.stopPropagation();
                            setPopoverVisible(!popoverVisible);
                        } }) }) }))] }));
    // 创建组合的extra内容，不再包含列设置按钮
    const combinedExtra = (_jsxs(Space, { children: [renderButtons(), extra] }));
    // 创建缺省操作列
    const createDefaultActionColumn = () => {
        if (!defaultActions)
            return null;
        const { onEdit, onDelete, onTest, editIcon, deleteIcon, testIcon, width = 120, spacing = 16, } = defaultActions;
        // 如果没有提供任何操作回调，则不创建操作列
        if (!onEdit && !onDelete && !onTest)
            return null;
        return {
            title: "操作",
            key: "defaultActions",
            width,
            fixed: "right",
            render: (_, record) => {
                const actions = [];
                if (onEdit) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: editIcon || React.createElement(EditOutlined), onClick: () => onEdit(record), style: { padding: 0, fontSize: "12px" } }, "edit"));
                }
                if (onTest) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: testIcon || React.createElement(SettingOutlined), onClick: () => onTest(record), style: { padding: 0, fontSize: "12px" } }, "test"));
                }
                if (onDelete) {
                    actions.push(_jsx(Button, { type: "link", size: "small", danger: true, icon: deleteIcon || React.createElement(DeleteOutlined), onClick: () => onDelete(record), style: { padding: 0, fontSize: "12px" } }, "delete"));
                }
                return _jsx(Space, { size: spacing, children: actions });
            },
        };
    };
    // 获取缺省操作列
    const defaultActionColumn = createDefaultActionColumn();
    // 合并列配置
    const getFinalColumns = () => {
        const baseColumns = resizableColumns ? resizedColumns : visibleColumns;
        // 如果已经有操作列，则不添加缺省操作列
        const hasActionColumn = baseColumns.some((col) => col.key === "action" ||
            col.key === "defaultActions" ||
            (typeof col.title === "string" && col.title.includes("操作")));
        if (defaultActionColumn && !hasActionColumn) {
            return [...baseColumns, defaultActionColumn];
        }
        return baseColumns;
    };
    // 构建最终的表格属性
    const finalTableProps = {
        ...tableProps,
        rowSelection: showSelection
            ? {
                selectedRowKeys,
                onChange: handleSelectionChange,
                ...(tableProps.rowSelection || {}),
            }
            : undefined,
        loading,
        pagination: currentPagination,
        onChange: handleTableChange,
        // 使用合并后的列配置
        columns: getFinalColumns(),
        components,
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
          /* 可调整列宽样式 */
          .react-resizable {
            position: relative;
            background-clip: padding-box;
          }

          .react-resizable-handle {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 8px;
            height: 100%;
            cursor: col-resize;
            z-index: 100;
            background: transparent;
          }

          /* 增强悬停效果 */
          .react-resizable-handle:hover {
            background-color: rgba(24, 144, 255, 0.15);
          }

          /* 拖拽时显示的辅助线 - 更加明显 */
          .react-resizable-handle:hover::after,
          .react-resizable-handle-active::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            right: 3px;
            width: 2px;
            background-color: #1890ff;
            opacity: 1;
          }

          /* 列头单元格样式调整 */
          th.ant-table-cell {
            position: relative !important;
          }
        ` }), _jsx("div", { style: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }, children: _jsx(Card, { title: titleWithSettings, extra: combinedExtra, styles: {
                        ...cardProps.styles,
                        body: {
                            padding: "16px",
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            ...cardProps.styles?.body,
                        },
                        // 确保 headerPadding 不被 cardProps.styles 覆盖
                        header: headerPadding
                            ? { padding: headerPadding }
                            : cardProps.styles?.header,
                    }, style: {
                        margin: "0",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        ...cardProps.style,
                    }, ...cardProps, children: _jsx("div", { style: { flex: 1, minHeight: 0 }, children: _jsx(Table, { ...finalTableProps }) }) }) })] }));
}
export default GenericPanelTable;
